import { useEffect, useState, useCallback, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { surveyApi, ApiResponseError } from '../services/api'
import type { Survey } from '../types/survey'
import { SurveyRenderer } from '../components/SurveyRenderer'
import { Loading } from '../components/Loading'
import '../styles/survey-page.css'

type Respondent = {
  respondentId: string
  token: string
  surveyId: string
  firstName: string
  lastName: string
  email?: string
  phone?: string
}

type PageState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'not-enabled' }
  | { status: 'ready'; survey: Survey }
  | { status: 'submitting' }
  | { status: 'submit-error'; message: string }

export function SurveyPage() {
  const { surveyId } = useParams<{ surveyId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [state, setState] = useState<PageState>({ status: 'loading' })
  const [respondent, setRespondent] = useState<Respondent | null>(null)
  const [regForm, setRegForm] = useState({ firstName: '', lastName: '' })
  const [regBusy, setRegBusy] = useState(false)
  const [regError, setRegError] = useState<string | null>(null)
  const startedAt = useMemo(() => new Date().toISOString(), [])

  useEffect(() => {
    if (!surveyId) {
      setState({ status: 'error', message: 'No survey ID provided.' })
      return
    }

    const token = new URLSearchParams(location.search).get('token')

    let cancelled = false
    Promise.all([
      surveyApi.getSurvey(surveyId),
      token ? surveyApi.verifyRespondentToken(token) : Promise.resolve(null),
    ])
      .then(([survey, verification]) => {
        if (cancelled) return
        setState({ status: 'ready', survey })
        if (verification && verification.surveyId === surveyId) {
          setRespondent(verification)
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          if (err instanceof ApiResponseError && err.status === 403) {
            setState({ status: 'not-enabled' })
          } else {
            setState({ status: 'error', message: err.message })
          }
        }
      })

    return () => {
      cancelled = true
    }
  }, [surveyId, location.search])

  const handleRegister = useCallback(async () => {
    if (!surveyId) return
    if (!regForm.firstName.trim() || !regForm.lastName.trim()) {
      setRegError('First and last name are required.')
      return
    }
    setRegBusy(true)
    setRegError(null)
    try {
      const created = await surveyApi.registerRespondent(surveyId, {
        firstName: regForm.firstName.trim(),
        lastName: regForm.lastName.trim(),
      }) as { respondentId: string; token: string; deduped?: boolean }
      if (created.deduped) {
        setRegError('A respondent with that name is already registered for this survey.')
        return
      }
      setRespondent({
        respondentId: created.respondentId,
        token: created.token,
        surveyId,
        firstName: regForm.firstName.trim(),
        lastName: regForm.lastName.trim(),
      })
      navigate(`/survey/${encodeURIComponent(surveyId)}?token=${encodeURIComponent(created.token)}`, { replace: true })
    } catch (err) {
      setRegError(err instanceof Error ? err.message : 'Could not register respondent.')
    } finally {
      setRegBusy(false)
    }
  }, [surveyId, regForm, navigate])

  const handleComplete = useCallback(
    async (data: Record<string, unknown>, durationMs: number) => {
      if (!surveyId || !respondent?.respondentId) return
      setState({ status: 'submitting' })
      try {
        await surveyApi.submitResponse(surveyId, respondent.respondentId, data, { startedAt, durationMs })
        navigate('/thank-you', { replace: true })
      } catch (err) {
        setState({
          status: 'submit-error',
          message: err instanceof Error ? err.message : 'Submission failed.',
        })
      }
    },
    [surveyId, respondent, startedAt, navigate],
  )

  const handleError = useCallback((err: Error) => {
    setState({ status: 'error', message: err.message })
  }, [])

  if (state.status === 'loading') return <Loading message="Loading survey..." />
  if (state.status === 'not-enabled') {
    return (
      <main className="survey-page-not-enabled" role="status">
        <h1>This survey is not enabled</h1>
      </main>
    )
  }
  if (state.status === 'error') {
    return (
      <main className="survey-page-error" role="alert">
        <h1>Unable to Load Survey</h1>
        <p>{state.message}</p>
        <button onClick={() => navigate('/')}>Back to home</button>
      </main>
    )
  }
  if (state.status === 'submitting') return <Loading message="Submitting your responses..." />
  if (state.status === 'submit-error') {
    return (
      <main className="survey-page-error" role="alert">
        <h1>Submission Failed</h1>
        <p>{state.message}</p>
        <button onClick={() => window.location.reload()}>Try again</button>
      </main>
    )
  }

  return (
    <main className="survey-page-container">
      {!respondent ? (
        <section className="respondent-card glass-card">
          <h1>Register to Begin</h1>
          <p>Enter your details and we’ll generate your survey invite token.</p>
          <div className="respondent-form">
            <label>
              First name *
              <input value={regForm.firstName} onChange={(e) => setRegForm((p) => ({ ...p, firstName: e.target.value }))} placeholder="First name" />
            </label>
            <label>
              Last name *
              <input value={regForm.lastName} onChange={(e) => setRegForm((p) => ({ ...p, lastName: e.target.value }))} placeholder="Last name" />
            </label>
            {regError ? <p className="respondent-error">{regError}</p> : null}
            <button onClick={handleRegister} disabled={regBusy}>{regBusy ? 'Registering…' : 'Continue'}</button>
          </div>
        </section>
      ) : (
        <section className="respondent-banner glass-card">
          <p>Respondent: <strong>{respondent.firstName} {respondent.lastName}</strong></p>
        </section>
      )}

      {respondent ? (
        <section className="survey-renderer-wrap">
          <SurveyRenderer survey={state.survey} onComplete={handleComplete} onError={handleError} />
        </section>
      ) : null}
    </main>
  )
}
